import { FC } from "react";
import { NumericInput } from "react-migration/components/NumericInput";
import debounce from "lodash/debounce";

import { MeasurementSystem } from "src/js/stores/user/store";
import { AllGeoJSON } from "src/js/util/turf";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import {
  convertUserUnitsToMeters,
  metersToWalkingMinutes,
  walkingMinutesToMeters,
} from "react-migration/lib/util/conversion";
import { radiusInUserUnits, unitOfMeasurement } from "react-migration/lib/util/measurements";
import { DrawingItem } from "src/js/types/DrawingItem";
import { DrawingsKeyboardEvent } from "react-migration/domains/comparables/typings/Drawings";

interface DrawingItemPoint {
  geometry: GeoJSON.Point;
}

export type RadiusInputProps = {
  updateGeometry: (scale: number, drawing: AllGeoJSON) => void;
  onChange?: (value: number) => void;
  drawing: DrawingItem | DrawingItemPoint | null | undefined;
  isSmallScale: boolean;
  unitPreference: MeasurementSystem;
  getRadius: (drawing: DrawingItem | DrawingItemPoint | null | undefined) => number;
};

export const RadiusInput: FC<RadiusInputProps> = ({
  updateGeometry,
  onChange,
  drawing,
  isSmallScale,
  unitPreference,
  getRadius,
}) => {
  const { t } = useTranslation();

  const onRadiusInputHandler = debounce((value, drawing) => {
    if (value && drawing) {
      updateGeometry(
        // use a rounded value to avoid floating point errors
        value /
          (Math.round(radiusInUserUnits({ radius: getRadius(drawing), unitPreference, isSmallScale }) * 100) / 100),
        drawing
      );
    }
    onChange?.(convertUserUnitsToMeters(value, { unitPreference, smallUnitSize: isSmallScale }));
  }, 100);

  const onWalkingInputHandler = debounce((value, drawing) => {
    const distance = walkingMinutesToMeters(value);
    if (value && drawing) {
      updateGeometry(distance / getRadius(drawing), drawing);
    }
    onChange?.(distance);
  }, 100);

  const handleOnKeyDown = (event: DrawingsKeyboardEvent) => {
    if (event.key === "Enter" && drawing && event.currentTarget.id === "walking-input") {
      onWalkingInputHandler(Number(event.currentTarget.value), drawing);
    }
    if (event.key === "Enter" && drawing && event.currentTarget.id === "radius-input") {
      onRadiusInputHandler(Number(event.currentTarget.value), drawing);
    }
  };

  return (
    <div className={"flex flex-row items-center"} data-testid="radius-input-wrapper">
      <NumericInput
        className={"bg-white  text-text-base-primary"}
        id="radius-input"
        data-testid="radius-input"
        isDisabled={!drawing}
        step={isSmallScale ? 1 : 0.1}
        onChange={(v) => onRadiusInputHandler(v, drawing)}
        value={
          Math.round(
            (radiusInUserUnits({ radius: getRadius(drawing), unitPreference, isSmallScale }) + Number.EPSILON) *
              (isSmallScale ? 1 : 100)
          ) / (isSmallScale ? 1 : 100)
        }
        units={unitOfMeasurement({ unitPreference, small: isSmallScale })}
        onKeyDown={handleOnKeyDown}
      />
      <span className={"mx-2 "} />
      <NumericInput
        className={"bg-white  text-text-base-primary"}
        id="walking-input"
        data-testid="walking-input"
        isDisabled={!drawing}
        step={0.1}
        onChange={(v) => onWalkingInputHandler(v, drawing)}
        value={Number(metersToWalkingMinutes(getRadius(drawing)).toFixed(1))}
        units={t("comparables.calculator.drawingTools.walking.unit")}
        onKeyDown={handleOnKeyDown}
      />
    </div>
  );
};
